import { main_url } from "../pages/basefile";
import { getValidToken } from "../auth/getToken";

const API_BASE = "/api/v1/questionnaires";

async function getAuthHeaders() {
  const token = (await getValidToken()) || localStorage.getItem("authToken");
  if (!token) {
    throw new Error("Authentication required. Redirecting to login.");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

async function handleResponse(response) {
  if (!response.ok) {
    const errorData =
      response.headers.get("content-type")?.includes("application/json") &&
      (await response.json());
    throw new Error(
      errorData?.detail ||
        errorData?.message ||
        `HTTP ${response.status}: ${response.statusText}`
    );
  }
  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    return response.json();
  }
  return response;
}

/**
 * Generate a unique questionnaire ID from identifier + display label.
 * @param {string} name - Questionnaire identifier
 * @param {string} label - Display name / form heading
 * @returns {Promise<Object>} - { qid: string }
 */
export const generateQuestionnaireQid = async (name, label) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams({ name, label });
  const response = await fetch(
    `${main_url}${API_BASE}/generate-qid?${params}`,
    { headers }
  );
  return handleResponse(response);
};

/**
 * Fetch questionnaires for datagrid with pagination, search, and sorting
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (0-indexed)
 * @param {number} params.pageSize - Items per page
 * @param {string} params.search - Search query
 * @param {string} params.sortField - Field to sort by
 * @param {string} params.sortOrder - Sort order (asc/desc)
 * @returns {Promise<Object>} - Datagrid response with rows and metadata
 */
export const fetchQuestionnaireDatagridRows = async ({
  page = 0,
  pageSize = 20,
  search = "",
  sortField = "qid",
  sortOrder = "desc",
}) => {
  const headers = await getAuthHeaders();
  const queryParams = new URLSearchParams({
    page,
    pageSize,
    sortField,
    sortOrder,
  });
  if (search) queryParams.append("search", search);

  const response = await fetch(
    `${main_url}${API_BASE}/datagrid/rows?${queryParams}`,
    { headers }
  );
  return handleResponse(response);
};

/**
 * Get all questionnaires for select/dropdown
 * @returns {Promise<Array>} - Array of questionnaires with qid, name, label
 */
export const fetchQuestionnairesForSelect = async () => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${main_url}${API_BASE}/select-all`, {
    headers,
  });
  return handleResponse(response);
};

/**
 * Get a single questionnaire by ID
 * @param {string} qid - Questionnaire ID
 * @returns {Promise<Object>} - Questionnaire data
 */
export const fetchQuestionnaire = async (qid) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${main_url}${API_BASE}/${qid}`, { headers });
  return handleResponse(response);
};

/**
 * Create a new questionnaire
 * @param {Object} payload - Questionnaire data
 * @param {string} payload.name - Unique identifier
 * @param {string} payload.label - Display name
 * @param {Object} payload.question_schema - SurveyJS schema
 * @returns {Promise<Object>} - Created questionnaire
 */
export const createQuestionnaire = async (payload) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${main_url}${API_BASE}`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
 * Update a questionnaire
 * @param {string} qid - Questionnaire ID
 * @param {Object} payload - Update data
 * @param {string} payload.label - New label (optional)
 * @param {Object} payload.question_schema - New schema (optional)
 * @returns {Promise<Object>} - Updated questionnaire
 */
export const updateQuestionnaire = async (qid, payload) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${main_url}${API_BASE}/${qid}`, {
    method: "PUT",
    headers,
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
 * Delete a questionnaire
 * @param {string} qid - Questionnaire ID
 * @returns {Promise<void>}
 */
export const deleteQuestionnaire = async (qid) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${main_url}${API_BASE}/${qid}`, {
    method: "DELETE",
    headers,
  });
  if (!response.ok) {
    const errorData =
      response.headers.get("content-type")?.includes("application/json") &&
      (await response.json());
    throw new Error(
      errorData?.detail ||
        errorData?.message ||
        `HTTP ${response.status}: ${response.statusText}`
    );
  }
};

/**
 * Get paginated list of questionnaires with search
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-indexed)
 * @param {number} params.limit - Items per page
 * @param {string} params.search - Search query
 * @returns {Promise<Object>} - Paginated response
 */
export const listQuestionnaires = async ({
  page = 1,
  limit = 20,
  search = "",
}) => {
  const headers = await getAuthHeaders();
  const queryParams = new URLSearchParams({
    page,
    limit,
  });
  if (search) queryParams.append("search", search);

  const response = await fetch(`${main_url}${API_BASE}?${queryParams}`, {
    headers,
  });
  return handleResponse(response);
};
